import { isTimeRangeEditable } from '@/utils/TaskLockUtils';

const ERROR_TYPES = {
  ROW_VALIDATION: {
    key: 'row_validation',
    label: 'Row Validation Errors',
    icon: 'mdi-table-row',
    hint: [
      'One or more rows have missing or invalid data.',
      'Check that each row has a WBS code, a task title in the correct level column, and no gaps in the task hierarchy.',
    ],
  },
  UNRECOGNISED_TASK: {
    key: 'unrecognised_task',
    label: 'Unrecognised Task IDs',
    icon: 'mdi-identifier',
    hint: [
      'The spreadsheet contains Task IDs that do not exist in the current version of the chosen schedule. ',
      'This usually means the spreadsheet was exported from a different schedule, a previous version, or the tasks have since been deleted.',
      'Re-export the spreadsheet from the current schedule and reapply your changes. Or if you are sure everything is correct you will need to remove the missing Task IDs from the spreadsheet so they are treated as new tasks and new IDs will be generated on import.',
    ],
  },
  DUPLICATE_ID: {
    key: 'duplicate_id',
    label: 'Duplicate Task IDs',
    icon: 'mdi-content-duplicate',
    hint: [
      'Two or more rows share the same Task ID.',
      'Each task must have a unique ID. This can happen if rows were copied and pasted within the spreadsheet. Remove the duplicate rows or clear the Task ID column on the copies so new IDs are generated.',
    ],
  },
  DUPLICATE_WBS: {
    key: 'duplicate_wbs',
    label: 'Duplicate WBS Codes',
    icon: 'mdi-code-tags',
    hint: [
      'Two or more tasks share the same WBS code at the same level.',
      'Each WBS code must be unique within its parent. Check for rows that were copied without updating their WBS values.',
    ],
  },
  ORPHANED_TASK: {
    key: 'orphaned_task',
    label: 'Orphaned Tasks',
    icon: 'mdi-link-off',
    hint: [
      'Some tasks reference a parent WBS that does not exist.',
      'This usually means a parent row was deleted or its WBS code was changed without updating the child rows.',
      'Restore the missing parent or move the orphaned tasks under an existing parent.',
    ],
  },
  LOCK_VIOLATION: {
    key: 'lock_violation',
    label: 'Lock Violations',
    icon: 'mdi-lock-alert',
    hint: [
      'Some tasks have been locked by a project administrator and cannot be modified.',
      'Revert the changes to the locked fields or contact your administrator to unlock the tasks before re-importing.',
    ],
  },
  INVALID_CALENDAR: {
    key: 'invalid_calendar',
    label: 'Invalid Calendars',
    icon: 'mdi-calendar-alert',
    hint: [
      'The Calendar Override column contains codes that do not match any published project calendar.',
      'Check that the calendar code is correct and that the calendar is still current and published.',
    ],
  },
  MISSING_CALENDAR_OVERRIDE: {
    key: 'missing_calendar_override',
    label: 'Missing Calendar Overrides',
    icon: 'mdi-calendar-remove',
    hint: [
      'The spreadsheet includes the Calendar Override column, so every row must name a calendar code from the Calendars sheet.',
      'Fill in the blank cells, or remove the Calendar Override column entirely to keep existing task calendars and use the schedule defaults for new tasks.',
    ],
  },
  MISSING_START_DATE: {
    key: 'missing_start_date',
    label: 'Missing Start Dates',
    icon: 'mdi-calendar-question',
    hint: [
      'Some tasks have no start date on a tracking time range.',
      'Every scheduled (calendar, dependency or time tracking) time range must have a start date. Fill in the Start Date column for these rows; for Primavera P6 imports, check that the activity has a StartDate in the XML.',
    ],
  },
  GENERAL: {
    key: 'general',
    label: 'Other Errors',
    icon: 'mdi-alert-circle',
    hint: [
      'These errors did not fall into a specific category.',
      'Review each message for details.',
    ],
  },
};

export class ExcelProcessorValidation {
    constructor() {
      this.errors = [];
      this.seenErrorKeys = new Set();
    }

    addError(message, type = ERROR_TYPES.GENERAL, row = null) {
      // validateTasks() runs twice per import (nested + display hierarchies), so
      // the same task can be flagged twice. Collapse exact duplicates.
      const key = `${type?.key}|${row}|${message}`;
      if (this.seenErrorKeys.has(key)) return;
      this.seenErrorKeys.add(key);
      this.errors.push({ message, type, row });
    }
  
    validateRow(row, columnIndices, currentPath, autoGenerateWBS = false) {
      const {
        wbsIndex,
        taskLevelColumns,
        subtaskTitleIndex
      } = columnIndices;

      const wbsCode = wbsIndex !== -1 ? row[wbsIndex] : undefined;
      const hasSubtaskTitle = subtaskTitleIndex !== -1 &&
                              row[subtaskTitleIndex] !== undefined &&
                              row[subtaskTitleIndex] !== null &&
                              (typeof row[subtaskTitleIndex] === 'string' ?
                                row[subtaskTitleIndex].trim() !== '' :
                                !isNaN(row[subtaskTitleIndex]));

      // If this is a subtask row (has subtask title but no WBS), it's valid
      if (hasSubtaskTitle &&
          (wbsCode === undefined || wbsCode === null ||
          (typeof wbsCode === 'string' && wbsCode.trim() === '') ||
          (typeof wbsCode === 'number' && isNaN(wbsCode)))) {
        return true;
      }

      // If auto-generating WBS, skip WBS validation (will be generated)
      if (autoGenerateWBS && wbsIndex === -1) {
        // WBS will be auto-generated, just validate task title exists
        let level = -1;
        for (let i = taskLevelColumns.length - 1; i >= 0; i--) {
          if (row[taskLevelColumns[i]]) {
            level = i;
            break;
          }
        }

        if (level === -1) {
          this.addError('No task title found in any level.', ERROR_TYPES.ROW_VALIDATION, row.rowIndex);
          return false;
        }

        return true;
      }

      // Otherwise, validate as a regular task row
      if (wbsCode === undefined || wbsCode === null || (typeof wbsCode === 'string' && wbsCode.trim() === '') || (typeof wbsCode === 'number' && isNaN(wbsCode))) {
        this.addError('WBS code is missing or invalid.', ERROR_TYPES.ROW_VALIDATION, row.rowIndex);
        return false;
      }

      let level = -1;
      for (let i = taskLevelColumns.length - 1; i >= 0; i--) {
        if (row[taskLevelColumns[i]]) {
          level = i;
          break;
        }
      }

      if (level === -1) {
        this.addError('No task title found in any level.', ERROR_TYPES.ROW_VALIDATION, row.rowIndex);
        return false;
      }

      // Check if there's a gap in the hierarchy
      for (let i = 0; i < level; i++) {
        if (!currentPath[i]) {
          this.addError(`Invalid task hierarchy. Task at level ${level + 1} found, but level ${i + 1} is missing.`, ERROR_TYPES.ROW_VALIDATION, row.rowIndex);
          return false;
        }
      }
  
      return true;
    }

    validateAgainstLock(newTasks, existingTasks) {
        const existingTaskMap = new Map(existingTasks.map(task => [task.id, task]));
        const newTaskIds = this.collectTaskIds(newTasks);

        const checkTask = (newTask) => {
          const existingTask = existingTaskMap.get(newTask.id);

          if (existingTask) {
            // A lock flag only restricts editing when it is explicitly false.
            const lock = existingTask.lock || {};

            // Per-template time-range lock: block any change to start, end or
            // duration of a time range whose template is not listed in
            // lock.editableTimeRangeTemplateIds.
            const lockedTemplateIds = this.lockedTimeRangeTemplateIds(newTask, existingTask);
            if (lockedTemplateIds.length > 0) {
              const changedTitles = this.changedLockedTimeRanges(newTask.timeRanges, existingTask.timeRanges, lockedTemplateIds);
              if (changedTitles.length > 0) {
                this.addError(`Task "${newTask.title}" (ID: ${newTask.id}): start, end and duration cannot be changed for locked time range(s): ${changedTitles.join(', ')}.`, ERROR_TYPES.LOCK_VIOLATION, newTask.rowIndex);
              }
            }

            // structureEditable === false: block any change to the task's structure
            // or title. subtasks[] (governed by subtasksEditable) and
            // viewableLocationIds[] (always editable) are excluded.
            if (lock.structureEditable === false) {
              const changedFields = this.structuralFieldChanges(newTask, existingTask);
              if (changedFields.length > 0) {
                this.addError(`Task "${newTask.title}" (ID: ${newTask.id}): ${changedFields.join(', ')} cannot be changed because the task is structurally locked.`, ERROR_TYPES.LOCK_VIOLATION, newTask.rowIndex);
              }
            }

            // subtasksEditable === false: block any change to subtasks other
            // than itemId and leadByTeamId (an RDrive-only field that stays
            // editable per subtask even on profile-locked tasks).
            if (lock.subtasksEditable === false &&
                !this.subtasksEquivalent(newTask.subtasks, existingTask.subtasks)) {
              this.addError(`Task "${newTask.title}" (ID: ${newTask.id}): subtasks cannot be changed because the task's subtasks are locked.`, ERROR_TYPES.LOCK_VIOLATION, newTask.rowIndex);
            }
          }

          if (newTask.children && newTask.children.length > 0) {
            newTask.children.forEach(checkTask);
          }
        };

        newTasks.forEach(checkTask);

        // structureEditable === false: a structurally locked task may not be
        // deleted. Any existing locked task missing from the upload is treated as
        // a deletion by TaskScheduleValidator, so flag it as a lock violation.
        existingTasks.forEach((existingTask) => {
          const lock = existingTask.lock || {};
          if (lock.structureEditable === false && !newTaskIds.has(existingTask.id)) {
            this.addError(`Task "${existingTask.title}" (ID: ${existingTask.id}): cannot be deleted because the task is structurally locked.`, ERROR_TYPES.LOCK_VIOLATION);
          }
        });
      }

    // Template ids on either side's rows that the existing task's lock does
    // not allow editing. A lock without the ids array is legacy: the API
    // resolves it as fully editable, so nothing is locked.
    lockedTimeRangeTemplateIds(newTask, existingTask) {
      const templateIds = new Set(
        [...(existingTask.timeRanges || []), ...(newTask.timeRanges || [])]
          .map((row) => row.timeRangeTemplateId)
          .filter(Boolean),
      );
      return [...templateIds].filter((id) => !isTimeRangeEditable(existingTask.lock, id));
    }

    // Titles (falling back to ids) of the locked time ranges whose start, end
    // or duration differ between the uploaded and existing rows.
    // progress is intentionally not compared so progress can still be updated.
    changedLockedTimeRanges(newTimeRanges, existingTimeRanges, lockedTemplateIds) {
      const byTemplate = (rows) => new Map((rows || []).map((row) => [row.timeRangeTemplateId, row]));
      const newRows = byTemplate(newTimeRanges);
      const existingRows = byTemplate(existingTimeRanges);
      const changed = [];
      lockedTemplateIds.forEach((templateId) => {
        const newRow = newRows.get(templateId);
        const existingRow = existingRows.get(templateId);
        if (!newRow && !existingRow) return;
        const equivalent = !!newRow && !!existingRow &&
          this.datesEquivalent(newRow.startDate, existingRow.startDate) &&
          this.datesEquivalent(newRow.endDate, existingRow.endDate) &&
          (newRow.duration?.value ?? null) === (existingRow.duration?.value ?? null) &&
          (newRow.duration?.unit ?? null) === (existingRow.duration?.unit ?? null);
        if (!equivalent) {
          changed.push(existingRow?.timeRangeTemplateTitle || newRow?.timeRangeTemplateTitle || templateId);
        }
      });
      return changed;
    }

    // Collect the ids of every task in a nested task tree.
    collectTaskIds(tasks, ids = new Set()) {
      (tasks || []).forEach((task) => {
        ids.add(task.id);
        if (task.children && task.children.length > 0) {
          this.collectTaskIds(task.children, ids);
        }
      });
      return ids;
    }

    // Structural fields that differ between the uploaded and existing task.
    // Excludes subtasks[] and viewableLocationIds[] (per the structure-lock
    // definition) and timeRanges (governed by its own lock).
    structuralFieldChanges(newTask, existingTask) {
      const changed = [];
      if (!this.structureFieldsEquivalent(newTask.title, existingTask.title)) changed.push('title');
      if (!this.structureFieldsEquivalent(newTask.code, existingTask.code)) changed.push('code');
      if (!this.wbsEquivalent(newTask.wbs, existingTask.wbs)) changed.push('WBS');
      if (!this.structureFieldsEquivalent(newTask.parentTaskId, existingTask.parentTaskId)) changed.push('parent task');
      return changed;
    }

    wbsEquivalent(a, b) {
      if (a === b) return true;
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;
      return a.every((segment, i) => this.structureFieldsEquivalent(segment, b[i]));
    }

    // Two subtask arrays are equivalent when they hold the same subtasks, in the
    // same order, with every field other than itemId unchanged. itemId is ignored
    // because linking/unlinking items stays allowed even when subtasks are locked.
    subtasksEquivalent(a, b) {
      const newSubtasks = a || [];
      const existingSubtasks = b || [];
      if (newSubtasks.length !== existingSubtasks.length) return false;
      return newSubtasks.every((subtask, i) => this.subtaskEquivalent(subtask, existingSubtasks[i]));
    }

    subtaskEquivalent(a, b) {
      if (a === b) return true;
      if (!a || !b) return false;
      const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
      keys.delete('itemId');
      keys.delete('leadByTeamId');
      for (const key of keys) {
        if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) return false;
      }
      return true;
    }

    structureFieldsEquivalent(a, b) {
      const normalize = (v) => (v === null || v === undefined) ? '' : String(v).trim();
      return normalize(a) === normalize(b);
    }

    datesEquivalent(a, b) {
      if (a === b) return true;
      if (!a || !b) return a == b;
      const tA = new Date(a).getTime();
      const tB = new Date(b).getTime();
      if (isNaN(tA) || isNaN(tB)) return false;
      return tA === tB;
    }
  
    validateTaskIdExists(taskId, rowIndex, existingTasksById) {
      if (taskId && !existingTasksById[taskId]) {
        this.addError(`Task ID "${taskId}" does not exist in the current project.`, ERROR_TYPES.UNRECOGNISED_TASK, rowIndex);
        return false;
      }
      return true;
    }

    addCalendarError(calendarCode, rowIndex = null) {
      this.addError(`Calendar code "${calendarCode}" does not match any published project calendar.`, ERROR_TYPES.INVALID_CALENDAR, rowIndex);
    }

    addMissingCalendarOverrideError(rowIndex = null) {
      this.addError('The Calendar Override cell is blank.', ERROR_TYPES.MISSING_CALENDAR_OVERRIDE, rowIndex);
    }

    validateTasks(tasks, existingTasks) {
      this.checkDuplicateIds(tasks);
      this.checkDuplicateWBS(tasks);
      this.validateWBSStructure(tasks);
      this.validateAgainstLock(tasks, existingTasks);
      this.validateTimeRangeCalendars(tasks);
      this.validateTimeRangeStartDates(tasks);
    }

    // The API rejects a commit when a tracking (non-'none') time range has no
    // calendarId, because duration weighting is normalized against that
    // calendar on the server. Surface it here instead of failing the commit.
    // The API requires a startDate on every tracking (non-'none') time range;
    // 'none' ranges have no scheduling semantics and may omit it. Also run for
    // P6 XML imports (SpreadsheetProcessor.processXMLFile), which skip
    // validateTasks.
    validateTimeRangeStartDates(tasks) {
      const checkTask = (task) => {
        (task.timeRanges || []).forEach(timeRange => {
          if (timeRange.timeRangeTemplateType !== 'none' && !timeRange.startDate) {
            this.addError(`Task "${task.title}" (ID: ${task.id}): no start date is set on its "${timeRange.timeRangeTemplateId}" time range. Tracking time ranges must have a start date.`, ERROR_TYPES.MISSING_START_DATE, task.rowIndex);
          }
        });
        (task.children || []).forEach(checkTask);
      };

      tasks.forEach(checkTask);
    }

    validateTimeRangeCalendars(tasks) {
      const checkTask = (task) => {
        (task.timeRanges || []).forEach(timeRange => {
          if (timeRange.timeRangeTemplateType !== 'none' && !timeRange.calendarId) {
            this.addError(`Task "${task.title}" (ID: ${task.id}): no calendar is assigned to its "${timeRange.timeRangeTemplateId}" time range. Tracking time ranges must reference a project calendar.`, ERROR_TYPES.INVALID_CALENDAR, task.rowIndex);
          }
        });
        (task.children || []).forEach(checkTask);
      };

      tasks.forEach(checkTask);
    }
  
    checkDuplicateIds(tasks) {
      // Track the first row each id appeared on so a duplicate can point at both
      // the offending row and the original.
      const seenRowById = new Map();

      const checkTask = (task) => {
        if (seenRowById.has(task.id)) {
          const firstRow = seenRowById.get(task.id);
          const partner = firstRow != null ? ` (first seen in row ${firstRow})` : '';
          this.addError(`Duplicate task ID: ${task.id}${partner}`, ERROR_TYPES.DUPLICATE_ID, task.rowIndex);
        } else {
          seenRowById.set(task.id, task.rowIndex);
        }
        if (task.children && task.children.length > 0) {
          task.children.forEach(checkTask);
        }
      };

      tasks.forEach(checkTask);
    }
  
    checkDuplicateWBS(tasks) {
      const wbsMap = new Map();
      
      const checkTask = (task) => {
        // Skip if this is a subtask (no WBS) or WBS is empty this was already validated in validateRow()
        if (!task.wbs || task.wbs.length === 0 || 
            (task.wbs.length === 1 && (!task.wbs[0] || task.wbs[0] === ''))) {
          return;
        }
        
        const wbsKey = JSON.stringify(task.wbs);
        if (wbsMap.has(wbsKey)) {
          this.addError(`Duplicate WBS: ${task.wbs.join('.')}. Tasks: "${wbsMap.get(wbsKey).title}" and "${task.title}"`, ERROR_TYPES.DUPLICATE_WBS, task.rowIndex);
        } else {
          wbsMap.set(wbsKey, task);
        }
        
        if (task.children && task.children.length > 0) {
          task.children.forEach(checkTask);
        }
      };
    
      tasks.forEach(checkTask);
    }
  
    validateWBSStructure(tasks) {
        const wbsMap = new Map();
        
        const checkTask = (task) => {
          const wbsString = task.wbs.join('');
          wbsMap.set(wbsString, task);
          
          // Check if parent exists
          if (task.wbs.length > 1) {
            const parentWBS = task.wbs.slice(0, -1).join('');
            if (!wbsMap.has(parentWBS)) {
              this.addError(`Task "${task.title}" (WBS: ${task.wbs.join('.')}) is orphaned. Parent WBS ${task.wbs.slice(0, -1).join('.')} does not exist.`, ERROR_TYPES.ORPHANED_TASK, task.rowIndex);
            }
          }
          
          // Check for gaps in WBS
          /*const lastWBSPart = parseInt(task.wbs[task.wbs.length - 1]);
          if (!isNaN(lastWBSPart) && lastWBSPart > 1) {
            const previousWBS = [...task.wbs.slice(0, -1), (lastWBSPart - 1).toString()].join('');
            if (!wbsMap.has(previousWBS)) {
              this.errors.push(`Gap detected in WBS structure before ${task.wbs.join('.')}. Expected ${[...task.wbs.slice(0, -1), (lastWBSPart - 1).toString()].join('.')} to exist.`);
            }
          }*/
          
          if (task.children && task.children.length > 0) {
            task.children.forEach(checkTask);
          }
        };
      
        tasks.forEach(checkTask);
      }
  
    getErrors() {
      return this.errors.map(e => e.message);
    }

    getGroupedErrors() {
      const groups = [];
      const groupMap = new Map();

      for (const error of this.errors) {
        const key = error.type.key;
        if (!groupMap.has(key)) {
          const group = { ...error.type, errors: [] };
          groupMap.set(key, group);
          groups.push(group);
        }
        groupMap.get(key).errors.push({ row: error.row, message: error.message });
      }

      return groups;
    }

    clearErrors() {
      this.errors = [];
      this.seenErrorKeys = new Set();
    }
  }